import { Box, HStack, Text, VStack } from '@chakra-ui/react'
import StreakFlameIcon from 'assets/icons/streak-flame.svg?react'
import { PLAY_SURFACE_BG, type RallyDayPlan } from '../mock-data'

interface IStreakBannerProps {
  plan: RallyDayPlan
  /**
   * `card` is the standalone surface used on mobile and wherever the streak owns
   * its own row. `compact` drops the card chrome and the subtitle so the streak can
   * sit beside the greeting in the desktop banner header without competing with it.
   */
  variant?: 'card' | 'compact'
}

const StreakBanner = ({ plan, variant = 'card' }: IStreakBannerProps) => {
  if (variant === 'compact') {
    return (
      <HStack spacing="12px" align="center">
        <Box
          w="32px"
          h="32px"
          borderRadius="9px"
          bg="#63E3F9"
          color="#000"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
        >
          <StreakFlameIcon width={18} height={18} />
        </Box>

        <Text color="white" fontSize="14px" fontWeight={600} m={0} lineHeight="1.2" flexShrink={0}>
          {plan.streakDays}
          <Box as="span" display="block" color="#F2F7FA" fontSize="12px" fontWeight={400} lineHeight="16px">
            day streak
          </Box>
        </Text>

        <HStack spacing="8px" flexShrink={0} align="flex-start">
          {plan.weekDots.map((dot, i) => (
            <VStack key={`${dot.label}-${i}`} spacing="5px">
              <Box
                w="9px"
                h="9px"
                borderRadius="2px"
                transform="rotate(45deg)"
                bg={dot.active ? '#63E3F9' : 'transparent'}
                border="1px solid"
                borderColor={dot.active ? '#63E3F9' : 'rgba(255,255,255,0.28)'}
              />
              <Text color="rgba(255,255,255,0.45)" fontSize="10px" fontWeight={600} m={0} lineHeight="1">
                {dot.label}
              </Text>
            </VStack>
          ))}
        </HStack>
      </HStack>
    )
  }

  return (
    <Box
      bg={PLAY_SURFACE_BG}
      border="1px solid"
      borderColor="rgba(107,121,143,0.14)"
      borderRadius="14px"
      p="14px"
    >
      <HStack spacing="12px" align="center">
        <Box
          w="36px"
          h="36px"
          borderRadius="10px"
          bg="#63E3F9"
          color="#000"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
        >
          <StreakFlameIcon width={20} height={20} />
        </Box>

        <VStack spacing="2px" align="flex-start" flex={1} minW={0}>
          <Text color="white" fontSize="15px" fontWeight={600} m={0} lineHeight="1.2">
            {plan.streakDays} day streak
          </Text>
          <Text color="rgba(255,255,255,0.55)" fontSize="12px" m={0} lineHeight="1.2">
            Log today's work to keep it rolling
          </Text>
        </VStack>

        <HStack spacing="10px" flexShrink={0} ml={{ md: 'auto' }} align="flex-start">
          {plan.weekDots.map((dot, i) => (
            <VStack key={`${dot.label}-${i}`} spacing="6px">
              {/* Rotated square = diamond marker; filled when the day has a completion. */}
              <Box
                w="10px"
                h="10px"
                borderRadius="2px"
                transform="rotate(45deg)"
                bg={dot.active ? '#63E3F9' : 'transparent'}
                border="1px solid"
                borderColor={dot.active ? '#63E3F9' : 'rgba(255,255,255,0.28)'}
              />
              <Text color="rgba(255,255,255,0.45)" fontSize="10px" fontWeight={600} m={0} lineHeight="1">
                {dot.label}
              </Text>
            </VStack>
          ))}
        </HStack>
      </HStack>
    </Box>
  )
}

export default StreakBanner
